/* @flow */

import React, { Fragment, useState, useEffect } from "react";
import { captureException } from "@sentry/browser";
import type { RootState, Action } from "../types";
import type { RouterHistory } from "react-router-dom";
import { getMetaNodeValue } from "../../../signups/src/dom";
import type {
  FormBlurHandlerEventArg,
  FormChangeHandlerEventArg
} from "../../../signups/src/types";
import Grid from "../../../signups/src/components/Grid";
import { Whole, Half } from "../../../signups/src/components/GridItem";
import GroupedList from "../../../signups/src/components/GroupedList";
import { Checkbox } from "../../../signups/src/components/Checkbox";
import TextField from "../../../signups/src/components/TextField";
import Link from "../../../signups/src/components/Link";
import SelectField from "../../../signups/src/components/SelectField";
import Button from "../../../signups/src/components/Button";
import ErrorBlock from "../../../signups/src/components/ErrorBlock";
import HelpText from "../../../signups/src/components/HelpText";
import schema from "../validators/schemas/sandboxSignup";
import styles from "./Form.scss";
import ReCAPTCHA from "react-google-recaptcha";
import { CONTACT_SALES_LINK, RECAPTCHA_SITE_KEY } from "../../../common/index";

type Config = {
  recaptchaSiteKey: string
};

type FormProps = {
  store: RootState,
  countries: Array<string>,
  dispatch: Action => Action,
  config: Config,
  countryCode: string,
  history: RouterHistory
};

const Form = ({ store, dispatch, config, countryCode, history }: FormProps) => {
  const {
    formState: { values, errors },
    countries
  } = store;

  const {
    firstName,
    lastName,
    companyName,
    businessLocation,
    emailAddress,
    consent
  } = values;

  const { recaptchaSiteKey = RECAPTCHA_SITE_KEY } = config;

  const countryOptions = countries.map((c, key) => {
    return { text: c[0], key: key.toString() };
  });

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formState, setFormState] = useState({
    submitting: false,
    canSubmit: false,
    networkError: null,
    serverError: null
  });

  const validateSchema = (): boolean =>
    schema.isValidSync({
      firstName,
      lastName,
      companyName,
      businessLocation,
      emailAddress,
      consent
    });

  const handleSubmit = (): void => {
    setFormState({
      ...formState,
      submitting: true,
      networkError: null,
      serverError: null
    });

    const config = {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "content-type": "application/json",
        "X-CSRF-Token": String(getMetaNodeValue("csrf-token")),
        Accept: "application/json"
      },
      body: JSON.stringify(store.formState.values)
    };

    fetch(`${window.location.origin}${window.location.pathname}`, config)
      .then(handleResponse)
      .catch(handleError);
  };

  const handleResponse = response => {
    if (response.ok) {
      history.push("/signup/sandbox/thanks");
    } else {
      response.json().then(body => {
        handleError(body.message);
      });
    }
  };

  const handleError = error => {
    setFormState({
      ...formState,
      submitting: false,
      networkError: error
    });
    captureException(error);
  };

  const handleBlur = (event: FormBlurHandlerEventArg) => {
    const { name, value } = event.currentTarget;
    dispatch({
      type: "SANDBOX_SIGNUP_FIELD_BLUR",
      payload: { name, value }
    });
  };

  const handleChange = (event: FormChangeHandlerEventArg) => {
    const { name, value } = event.currentTarget;

    dispatch({
      type: "SANDBOX_SIGNUP_FIELD_CHANGE",
      payload: { name, value }
    });
  };

  const handleCheckboxBlur = (event: FormBlurHandlerEventArg) => {
    const { name } = event.currentTarget;

    dispatch({
      type: "SANDBOX_SIGNUP_CHECKBOX_BLUR",
      payload: { name, checked: consent }
    });
  };

  const handleCheckboxChange = (event: FormChangeHandlerEventArg) => {
    const { name } = event.currentTarget;

    dispatch({
      type: "SANDBOX_SIGNUP_CHECKBOX_CHANGE",
      payload: { name, checked: !consent }
    });
  };

  const handleCountryChange = (event: FormChangeHandlerEventArg) => {
    const { name, value } = event.currentTarget;
    const country = countries[Number(value)];
    dispatch({
      type: "SANDBOX_SIGNUP_COUNTRY_CHANGE",
      payload: {
        name,
        value: { text: country[0], code: country[1], key: value }
      }
    });
  };

  const handleCountryBlur = (event: FormBlurHandlerEventArg) => {
    const { name, value } = event.currentTarget;
    const country = countries[Number(value)];

    dispatch({
      type: "SANDBOX_SIGNUP_COUNTRY_BLUR",
      payload: {
        name,
        value: { text: country[0], code: country[1], key: value }
      }
    });
  };

  // Default USA country key
  const defaultCountryKey = countries.findIndex(
    c => c[1].toUpperCase() === "USA"
  );

  const setBusinessLocationKey = () => {
    const key = countries.findIndex(
      c => c[1].toUpperCase() === countryCode.toUpperCase()
    );

    // Use defaultCountryKey if key is -1 (not found)
    const resolvedKey = key !== -1 ? key : defaultCountryKey;

    // Get the country details using the resolved key
    const country = countries[resolvedKey];

    dispatch({
      type: "SANDBOX_SIGNUP_COUNTRY_CHANGE",
      payload: {
        name: "businessLocation",
        value: {
          text: country[0],
          code: country[1],
          key: resolvedKey.toString()
        }
      }
    });
  };

  useEffect(() => {
    setFormState({
      ...formState,
      canSubmit: validateSchema() && Boolean(recaptchaToken)
    });
  }, [values, recaptchaToken]);

  return (
    <GroupedList className={styles.root} displayAsCard={true}>
      <h2 className={styles.heading}>Sign up for the sandbox</h2>
      <form onSubmit={handleSubmit}>
        <Grid>
          <Half>
            <TextField
              name="firstName"
              errorText={errors.firstName}
              labelText="First Name"
              onBlur={handleBlur}
              onChange={handleChange}
              value={firstName}></TextField>
          </Half>
          <Half>
            <TextField
              name="lastName"
              errorText={errors.lastName}
              labelText="Last Name"
              onBlur={handleBlur}
              onChange={handleChange}
              value={lastName}></TextField>
          </Half>
          <Whole>
            <TextField
              name="companyName"
              errorText={errors.companyName}
              labelText="Company Name"
              onBlur={handleBlur}
              onChange={handleChange}
              value={companyName}></TextField>
          </Whole>
          <Whole>
            <SelectField
              errorText={errors.businessLocation}
              name="businessLocation"
              labelText="Where Is Your Business Located?"
              options={countryOptions}
              value={
                businessLocation.key
                  ? businessLocation.key
                  : setBusinessLocationKey()
              }
              onBlur={handleCountryBlur}
              onChange={handleCountryChange}></SelectField>
          </Whole>
          <Whole>
            <TextField
              errorText={errors.emailAddress}
              name="emailAddress"
              labelText="Email Address"
              onBlur={handleBlur}
              onChange={handleChange}
              value={emailAddress}></TextField>
          </Whole>
          <Whole>
            <Checkbox
              checked={consent}
              description="I agree that Braintree may contact me at the email address above."
              name="consent"
              onBlur={handleCheckboxBlur}
              onChange={handleCheckboxChange}
            />
          </Whole>
          <Whole>
            <ReCAPTCHA
              className={styles["recaptcha-container"]}
              sitekey={recaptchaSiteKey}
              onChange={token => {
                setRecaptchaToken(token);
              }}
            />
          </Whole>
          {Boolean(formState.serverError) && (
            <Whole>
              <ErrorBlock message="Sorry, an error exists in the data provided on this application. Please correct the error and try again." />
            </Whole>
          )}
          {Boolean(formState.networkError) && (
            <Whole>
              <ErrorBlock
                message={
                  <Fragment>
                    Sorry, there was an issue submitting your data. Please try
                    again, or{" "}
                    <Link
                      href={CONTACT_SALES_LINK}
                      intent="simple"
                      target="_blank">
                      contact our sales team
                    </Link>{" "}
                    for support.
                  </Fragment>
                }
              />
            </Whole>
          )}
          <Whole>
            <Button
              intent="primary"
              fullWidth={true}
              onClick={handleSubmit}
              loading={formState.submitting}
              disabled={!formState.canSubmit || formState.submitting}>
              Try the sandbox
            </Button>
          </Whole>
          {!formState.canSubmit && (
            <Whole>
              <HelpText alignTextCenter={true}>
                Your application is incomplete. Please fill out all fields
                before submitting.
              </HelpText>
            </Whole>
          )}
        </Grid>
      </form>
    </GroupedList>
  );
};

export default Form;
